import React from "react";
import { Autocomplete, type Option } from "../../../shared/components";
import { useAppSelector } from "../../../store";
import type { CycleEntry } from "../../../store/profiling/types";
import { ResizeSplit } from "./ResizeSplit";
import ProfileTimeline from "./ProfileTimeline";
import FlameGraph from "./FlameGraph";
import NodeDetail from "./NodeDetail";
import { useBatchSelection } from "./useBatchSelection";
import { shortCycleId, formatMs, summarizeCycle, cycleBatches, batchKey, type CycleSummary } from "./viz";

/**
 * Side-by-side comparison of two render cycles.  Entirely self-contained: each pane owns its cycle
 * choice and an independent `useBatchSelection`, and composes the same prop-driven detail stack
 * (timeline / flame graph / node detail) the Passes view uses — no comparison awareness in any of
 * those components.
 */

const cycleLabel = (c: CycleEntry) => `${shortCycleId(c.cycleId)} · ${c.fileName ?? "(inline)"}`;

// The parser-side total: main render walk + render-prop callback re-evaluations.
const parserTotal = (s: CycleSummary) => s.renderTotal + s.callbackTotal;

/**
 * Signed difference against the other pane's value, inlined next to a metric.  Positive (this pane
 * is larger/slower) reads amber, negative green; equal values render nothing.
 */
function Delta({ value, other, fmt }: { value: number; other: number | undefined; fmt: (n: number) => string }) {
    if (other === undefined) return null;
    const delta = value - other;
    if (delta === 0) return null;
    return (
        <span className={`stat-delta ${delta > 0 ? "delta-up" : "delta-down"}`}>
            ({delta > 0 ? "+" : "−"}{fmt(Math.abs(delta))})
        </span>
    );
}

function CyclePane({ cycle, other, label, options, onPick }: {
    cycle: CycleEntry | undefined;
    /** The opposite pane's cycle — enables the inline deltas when both sides are picked. */
    other: CycleEntry | undefined;
    label: string;
    options: Option[];
    onPick: (cycleId: string | null) => void;
}) {
    const { activeBatch, selectedNodeId, selectedNode, selectBatch, selectNode } = useBatchSelection(cycle);
    // Chip value from the cycle itself so a pick survives even if the cycle is later evicted.
    const value = cycle ? { value: cycle.cycleId, label: cycleLabel(cycle) } : null;
    const stats = cycle ? summarizeCycle(cycle) : null;
    const otherStats = other ? summarizeCycle(other) : undefined;

    return (
        <>
            <div className="compare-pane-header">
                <Autocomplete
                    size="small"
                    label={label}
                    options={options}
                    value={value}
                    onChange={(_e, v) => onPick((v as Option | null)?.value ?? null)}
                />
                {cycle && stats && (
                    <div className="compare-pane-stats">
                        <span>Wall {formatMs(stats.wallTime)} <Delta value={stats.wallTime} other={otherStats?.wallTime} fmt={formatMs} /></span>
                        <span>Parser {formatMs(parserTotal(stats))} <Delta value={parserTotal(stats)} other={otherStats && parserTotal(otherStats)} fmt={formatMs} /></span>
                        <span>
                            React {cycle.react ? formatMs(stats.reactTotal) : "off"}
                            {cycle.react && <> <Delta value={stats.reactTotal} other={otherStats?.reactTotal} fmt={formatMs} /></>}
                        </span>
                        <span>{stats.totalNodes.toLocaleString()} nodes <Delta value={stats.totalNodes} other={otherStats?.totalNodes} fmt={n => n.toLocaleString()} /></span>
                    </div>
                )}
            </div>
            {!cycle && <div className="cycle-detail-empty">Select a cycle to compare.</div>}
            {cycle && (
                <ResizeSplit className="detail-split" direction="vertical" initialSizes={[24, 76]} minSizes={[70, 160]}>
                    <div className="timeline-pane">
                        {/* No scope props — the Compare panes always show their own cycle's batches. */}
                        <ProfileTimeline
                            batches={cycleBatches(cycle)}
                            selectedBatchKey={activeBatch ? batchKey(activeBatch) : null}
                            onSelectBatch={selectBatch}
                            viewKey={cycle.cycleId}
                        />
                    </div>
                    {/* Half-width panes: stack the node detail below the flame graph instead of beside it. */}
                    <ResizeSplit className="flame-and-detail" direction="vertical" initialSizes={[62, 38]} minSizes={[140, 100]}>
                        <div className="flame-pane">
                            {activeBatch
                                ? <FlameGraph batch={activeBatch} selectedNodeId={selectedNodeId} onSelectNode={selectNode} />
                                : <div className="cycle-detail-empty">This cycle produced no timing nodes.</div>}
                        </div>
                        <div className="detail-pane">
                            {activeBatch && <NodeDetail batch={activeBatch} node={selectedNode} />}
                        </div>
                    </ResizeSplit>
                </ResizeSplit>
            )}
        </>
    );
}

type Props = {
    /** Controlled A/B choice — owned by the Profiling tab so the tree's context menu can set it. */
    cycleIds: { a: string | null; b: string | null };
    onChange: (side: "a" | "b", cycleId: string | null) => void;
};

function CompareView({ cycleIds, onChange }: Props) {
    const cyclesById = useAppSelector(s => s.profiling.cyclesById);
    const cycleA = cycleIds.a ? cyclesById[cycleIds.a] : undefined;
    const cycleB = cycleIds.b ? cyclesById[cycleIds.b] : undefined;

    const options = React.useMemo<Option[]>(
        () => Object.values(cyclesById)
            .toSorted((a, b) => b.startTime - a.startTime)
            .map(c => ({ value: c.cycleId, label: cycleLabel(c) })),
        [cyclesById]
    );

    return (
        <div className="compare-view">
            <ResizeSplit className="compare-columns" direction="horizontal" initialSizes={[50, 50]} minSizes={[320, 320]} gutterSize={8}>
                <div className="compare-pane">
                    <CyclePane cycle={cycleA} other={cycleB} label="Cycle A" options={options} onPick={id => onChange("a", id)} />
                </div>
                <div className="compare-pane">
                    <CyclePane cycle={cycleB} other={cycleA} label="Cycle B" options={options} onPick={id => onChange("b", id)} />
                </div>
            </ResizeSplit>
        </div>
    );
}

export default React.memo(CompareView);
